/**
 * supabaseCases.ts — minimale CRUD-Schicht für die `cases`-Tabelle (AP11).
 *
 * RLS aus AP2/AP7 sorgt automatisch dafür, dass nur eigene Cases (oder die
 * des eigenen Tenants bei Berater-Rollen) sichtbar sind. Wir setzen daher
 * keine zusätzlichen WHERE-Filter — sondern lassen Supabase entscheiden.
 *
 * Crypto: Diese Schicht bewegt KEINE sensiblen Felder (BWA, BG, Health-
 * Texte). Nur Stamm-/Status-Daten der `cases`-Tabelle. Sensible Daten
 * landen in `dimension_data` und `financial_dataset` — eine separate
 * verschlüsselte Persistenz-Schicht ist AP11b.
 */

import { getSupabaseClient } from './supabase';
import type { Database } from './supabaseTypes';

export type CaseRow = Database['public']['Tables']['cases']['Row'];

export interface CreateCaseInput {
  readonly type: 'b2c' | 'b2g';
  readonly legalBasis: 'par10' | 'par16b' | 'par16c' | 'par93';
  // Mandanten-Stammdaten
  readonly clientFullName?: string;
  readonly clientBirthdate?: string; // ISO YYYY-MM-DD
  readonly clientAddress?: string;
  // Geschäftsdaten (Migration 0009) — Stammdaten zum geprüften Unternehmen
  readonly businessBranch?: string;
  readonly businessLegalForm?: string;
  readonly businessStartDate?: string; // ISO YYYY-MM-DD
  readonly businessTaxId?: string;
  readonly businessDescription?: string;
  // Jobcenter-Daten (Pflicht bei B2G)
  readonly jobcenterName?: string;
  readonly jobcenterAddressLine1?: string;
  readonly jobcenterAddressLine2?: string;
  readonly jobcenterContactName?: string;
  readonly jobcenterFileNumber?: string;
  readonly orderDate?: string; // ISO YYYY-MM-DD
  // Termin-/Verfahrensdaten (alle optional, später nachziehbar)
  readonly benefitStartDate?: string; // ISO YYYY-MM-DD
  readonly dataSubmittedAt?: string; // ISO YYYY-MM-DD
  readonly meetingDate?: string; // ISO YYYY-MM-DD
  readonly meetingDurationMin?: number;
  readonly meetingFormat?: 'vor_ort' | 'video' | 'telefon';
  readonly additionalDocs?: string;
}

/**
 * Listet die Cases des eingeloggten Users (RLS: owner_id = auth.uid()
 * oder Berater-Tenant-Match).
 */
export async function listMyCases(): Promise<CaseRow[]> {
  const supa = getSupabaseClient();
  const { data, error } = await supa
    .from('cases')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listMyCases: ${error.message}`);
  return data;
}

/**
 * Legt einen neuen Case an. owner_id und tenant_id werden serverseitig
 * gesetzt (siehe Trigger `cases_set_tenant_id` in 0006_security_hardening).
 */
export async function createCase(input: CreateCaseInput): Promise<CaseRow> {
  const supa = getSupabaseClient();
  const { data: userData } = await supa.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) throw new Error('createCase: kein authentifizierter User');

  // Wir casten via `as never`, weil die handgepflegten Database-Types
  // (apps/web/src/lib/supabaseTypes.ts) den Insert-Typ konservativ als
  // Omit<CaseRow, …> definieren. Sobald `supabase gen types` läuft (post
  // Cloud-Setup), wird das durch die echten Insert-Types ersetzt.
  const insert = {
    type: input.type,
    legal_basis: input.legalBasis,
    status: 'in_progress' as const,
    owner_id: userId,
    // Mandanten-Stammdaten
    client_full_name: input.clientFullName ?? null,
    client_birthdate: input.clientBirthdate ?? null,
    client_address: input.clientAddress ?? null,
    // Geschäftsdaten (Migration 0009)
    business_branch: input.businessBranch ?? null,
    business_legal_form: input.businessLegalForm ?? null,
    business_start_date: input.businessStartDate ?? null,
    business_tax_id: input.businessTaxId ?? null,
    business_description: input.businessDescription ?? null,
    // Jobcenter-Daten
    jobcenter_name: input.jobcenterName ?? null,
    jc_address_line1: input.jobcenterAddressLine1 ?? null,
    jc_address_line2: input.jobcenterAddressLine2 ?? null,
    jc_contact_name: input.jobcenterContactName ?? null,
    jc_file_number: input.jobcenterFileNumber ?? null,
    order_date: input.orderDate ?? null,
    // Termin-/Verfahrensdaten
    benefit_start_date: input.benefitStartDate ?? null,
    data_submitted_at: input.dataSubmittedAt ?? null,
    meeting_date: input.meetingDate ?? null,
    meeting_duration_min: input.meetingDurationMin ?? null,
    meeting_format: input.meetingFormat ?? null,
    additional_docs: input.additionalDocs ?? null,
  };

  const { data, error } = await supa
    .from('cases')
    .insert(insert as never)
    .select('*')
    .single();
  if (error) {
    // Supabase liefert oft detaillierte Hints im error-Object — wir loggen
    // das vollständig in die Console, damit wir bei 400-Antworten den
    // Postgres-Constraint sehen, der gefeuert hat.
    console.error('createCase failed', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
      payload: insert,
    });
    const detail = error.details ? ` · ${error.details}` : '';
    const hint = error.hint ? ` · Hinweis: ${error.hint}` : '';
    throw new Error(`createCase [${error.code ?? '?'}]: ${error.message}${detail}${hint}`);
  }
  return data;
}

/**
 * Holt einen einzelnen Case. RLS regelt Sichtbarkeit.
 *
 * Defensive: Wenn caseId nicht UUID-Format hat (z.B. "demo" oder Tippfehler),
 * würde Postgres die `id`-Spalte (UUID) nicht casten können → 400 mit
 * kryptischer Meldung. Wir filtern das vorher raus und liefern null statt
 * einen Server-Roundtrip zu machen.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function getCase(caseId: string): Promise<CaseRow | null> {
  if (!UUID_RE.test(caseId)) {
    console.warn('getCase: caseId ist kein UUID-Format, returnt null:', caseId);
    return null;
  }
  const supa = getSupabaseClient();
  const { data, error } = await supa
    .from('cases')
    .select('*')
    .eq('id', caseId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) {
    console.error('getCase failed', {
      caseId,
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
    });
    const detail = error.details ? ` · ${error.details}` : '';
    const hint = error.hint ? ` · Hinweis: ${error.hint}` : '';
    throw new Error(`getCase [${error.code ?? '?'}]: ${error.message}${detail}${hint}`);
  }
  return data;
}
